import { creditsQueryOptions } from "@/api/credits.api";
import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { Button } from "@/components/generic/Button";
import { NumberField } from "@/components/generic/NumberField";
import {
  formatScheduleDays,
  getScheduleDays,
} from "@/services/schedule.service";
import { detectCurrentStrategy } from "@/utils/configurations";
import { formatNumber } from "@/utils/number-utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { Form } from "react-aria-components";
import { useForm } from "react-hook-form";
import z from "zod";

export const Route = createFileRoute("/_dashboard/campaigns/$id/(edit)/boost/lead-amount")(
  {
    component: RouteComponent,
    beforeLoad: async ({ context }) => {
      await Promise.all([
        context.queryClient.ensureQueryData(
          context.campaignRunMaxLeadsQueryOptions,
        ),
        context.queryClient.ensureQueryData(creditsQueryOptions),
      ]);
    },
  },
);

function RouteComponent() {
  const context = Route.useRouteContext();

  const historyState = context.useHistoryState();
  const stepper = context.useStepper();

  const { data: campaign } = useSuspenseQuery(context.campaignQueryOptions);
  const { data: maxLeads } = useSuspenseQuery(
    context.campaignRunMaxLeadsQueryOptions,
  );
  const { data: remainingLeads } = useSuspenseQuery({
    ...context.countsQueryOptions,
    select: (counts) => counts.reduce((sum, { count }) => sum + count, 0),
  });

  const schema = useMemo(() => {
    let schemaMax = 1000;
    let schemaMaxReason = `Kan ikke modtage over ${formatNumber(schemaMax)} leads ad gangen`;

    const maxRemainingLeads =
      maxLeads.reason === "NotEnoughLeads" ? maxLeads.max : remainingLeads;

    if (maxRemainingLeads < schemaMax) {
      schemaMax = maxRemainingLeads;
      schemaMaxReason = `Kan ikke modtage over ${formatNumber(schemaMax)} leads, da der ikke er flere i målgruppen`;
    }

    return z.object({
      amount: z
        .number("Indtast et antal")
        .min(1, "Skal være mindst én")
        .max(schemaMax, schemaMaxReason),
    });
  }, [remainingLeads, maxLeads.max, maxLeads.reason]);

  const defaultValues = useMemo(
    () => ({
      amount: stepper.context.leadAmount ?? undefined,
    }),
    [stepper.context.leadAmount],
  );

  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
    setValue,
  } = useForm<z.infer<typeof schema>>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (historyState) {
      setValue("amount", historyState.amount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyState]);

  const startCampaignRunMutation = context.useStartCampaignRunMutation();

  const submit = handleSubmit(async (data) => {
    stepper.setContext((prev) => ({ ...prev, leadAmount: data.amount }));

    if (stepper.nextStep) {
      stepper.goNext();
    } else {
      startCampaignRunMutation.mutateAsync({
        options: {
          amount: data.amount,
          deliverContacts: campaign.deliverContacts,
          deliverContactsRequirePhone: campaign.deliverContactsRequirePhone,
          deliverLiveNumber: campaign.deliverLiveNumber,
          completionEmail: campaign.completionEmail,
        },
        skipCreditCheck: autoSubmittedRef.current,
      });
    }
  });

  context.stepper.useStepperSubmit(
    useMemo(
      () => ({
        submit,
        isDisabled: !isValid || isSubmitting,
        isPending: isSubmitting,
      }),
      [isSubmitting, isValid, submit],
    ),
  );

  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (autoSubmittedRef.current) return;

    if (historyState?.autoSubmit) {
      autoSubmittedRef.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyState]);

  const currentStrategy = useMemo(
    () => detectCurrentStrategy(campaign.configurations),
    [campaign.configurations],
  );

  const enabledConfigurationsCount = useMemo(
    () => campaign.configurations.filter((c) => c.enabled && !c.deleted).length,
    [campaign.configurations],
  );

  const formattedScheduleDays = useMemo(
    () => formatScheduleDays(getScheduleDays(campaign.schedules)),
    [campaign.schedules],
  );

  return (
    <div className="flex flex-1">
      {maxLeads.max <= 0 &&
      (maxLeads.reason === "NotEnoughCredits" ||
        maxLeads.reason === "NotEnoughLeads") ? (
        <div className="flex size-full flex-col items-center justify-center gap-y-12">
          {(maxLeads.reason === "NotEnoughCredits" && (
            <div className="flex flex-col items-center justify-center gap-y-9">
              <SegmentHeader
                title="Ikke nok credits"
                description="Du har ikke nok credits til at lave en manuel levering for denne kampagne. Køb flere credits for at kunne modtage flere leads med det samme."
              />

              <Button
                animate
                link={{
                  from: Route.fullPath,
                  to: "/campaigns/$id/boost/buy-credits",
                }}
              >
                Køb flere credits
              </Button>
            </div>
          )) || (
            <SegmentHeader
              title="Ikke nok leads"
              description="Du kan ikke modtage flere leads, da du har modtaget alle i målgruppen."
            />
          )}
        </div>
      ) : (
        <Form
          className="mx-auto flex size-full max-w-xl flex-col items-center justify-center gap-y-12"
          onSubmit={submit}
        >
          <div className="flex flex-col items-center gap-y-10">
            <SegmentHeader
              className="max-w-md"
              title="Modtag leads med det samme"
              description={[
                `Indtast det antal leads du ønsker at modtage med det samme.${enabledConfigurationsCount > 1 ? ` Leveringen vil følge din valgte strategi (${currentStrategy.title}).` : ""}`,
                formattedScheduleDays &&
                  `Du vil fortsat modtage leads ${formattedScheduleDays}.`,
              ]
                .filter(Boolean)
                .join(" ")}
            />

            <NumberField
              className="w-full max-w-3xs"
              control={control}
              name="amount"
              aria-label="Antal leads"
              placeholder="Antal leads"
              isRequired
              maxDecimals={0}
            />
          </div>
        </Form>
      )}
    </div>
  );
}
